import { useMemo, useState } from 'react';
import { Button, Select, Tag } from 'antd';
import {
  StopOutlined, SearchOutlined,
  IdcardOutlined, TeamOutlined, FundProjectionScreenOutlined, AppstoreOutlined,
  TagsOutlined, BankOutlined, SwapOutlined,
  CheckOutlined, CloseOutlined, FlagFilled, WarningOutlined,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from 'styled-components';
import utils from './utils';
import { TYPE_OPTS } from './IntentTypeSelect';
import { intentTypeIcons } from '../config.js';
import { updateFilterData, clearFilterData } from '../slices/filters/thunk';

// Token format: "<filterKey>::<rawValue>" — encodes which filter a suggestion /
// chip belongs to and its raw value. Split on the first "::" only, so a raw value
// that itself contains "::" (e.g. free-text search) round-trips intact.
const SEP = '::';
const makeToken = (key, value) => `${key}${SEP}${value}`;
const parseToken = (token) => {
  const idx = token.indexOf(SEP);
  return { key: token.slice(0, idx), value: token.slice(idx + SEP.length) };
};

// Diacritic-insensitive normalize so "č/š/ž" match "c/s/z" (and vice-versa).
const normalize = (s) =>
  String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Group label shown both as a badge before each suggestion and as the prefix on
// the selected chip (e.g. "Oseba: Anja Ferjančič"). Icons mirror the ones used
// in the sidebar nav / old filters so the categories feel familiar.
const GROUP_COLORS = {
  Oseba: 'blue',
  Podjetje: 'geekblue',
  Projekt: 'purple',
  Kategorija: 'cyan',
  Oznaka: 'magenta',
  Račun: 'gold',
  'Tip transakcije': 'volcano',
  Plačano: 'green',
  Izdano: 'green',
  Stanje: 'red',
  Iskanje: 'default',
};

const GROUP_ICONS = {
  Oseba: <IdcardOutlined />,
  Podjetje: <TeamOutlined />,
  Projekt: <FundProjectionScreenOutlined />,
  Kategorija: <AppstoreOutlined />,
  Oznaka: <TagsOutlined />,
  Račun: <BankOutlined />,
  'Tip transakcije': <SwapOutlined />,
  Plačano: <CheckOutlined />,
  Izdano: <CheckOutlined />,
  Stanje: <FlagFilled />,
  Iskanje: <SearchOutlined />,
};

// Per-value icon for filters whose individual options carried their own icon in
// the old UI (intent type, paid/issued, status). Tags get a colour swatch.
const valueIconFor = (key, rawValue, data) => {
  if (key === 'type') return intentTypeIcons[rawValue] || null;
  if (key === 'is_paid' || key === 'is_issued') {
    return isTrue(rawValue)
      ? <CheckOutlined style={{ color: '#52c41a' }} />
      : <CloseOutlined style={{ color: '#ff4d4f' }} />;
  }
  if (key === 'status') {
    return rawValue === 'flagged'
      ? <FlagFilled style={{ color: '#ff4d4f' }} />
      : <WarningOutlined style={{ color: 'orange' }} />;
  }
  if (key === 'tags_filters' && data?.color_hex) {
    return (
      <span style={{
        display: 'inline-block', width: 10, height: 10, borderRadius: 2,
        background: data.color_hex,
      }} />
    );
  }
  return null;
};

// Tri-state (paid / issued) shared option set.
const YESNO = [
  { value: 1, title: 'Da' },
  { value: 0, title: 'Ne' },
];

// Status pseudo-filter maps a single chip onto the two leaf boolean filters.
const STATUS_VALUES = [
  { value: 'flagged', title: 'Označeno', filter: { is_flagged: 1, has_warning: null } },
  { value: 'warning', title: 'Z opozorilom', filter: { is_flagged: null, has_warning: 1 } },
];
const isTrue = (v) => v === '1' || v === 1;

// How many suggestions to render per "page". The full candidate pool lives
// client-side, so we window it and grow on scroll (infinite scroll) instead of
// hard-capping — typing a group name like "Projekt" can match hundreds of rows.
const PAGE_SIZE = 50;

// Filters whose "N/A" bucket is addressable -- i.e. the insights breakdowns that
// can produce a no-company / no-project / no-tag row. See utils.NO_VALUE.
const NO_VALUE_KEYS = new Set(['companies', 'projects', 'tags_filters']);
const NO_VALUE_LABEL = 'N/A';
const withNoValue = (key, opts) =>
  NO_VALUE_KEYS.has(key) ? [utils.noValueOption(NO_VALUE_LABEL), ...opts] : opts;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  .ant-select {
    flex: 1;
  }

  /* Keep the omni-search compact so it + the date range fit the fixed header. */
  .ant-select .ant-select-selector {
    min-height: 36px !important;
    border-radius: 8px !important;
    padding-inline: 10px !important;
  }
  .ant-select-selection-search-input,
  .ant-select-selection-placeholder,
  .ant-select-selection-overflow {
    font-size: 14px !important;
  }
  .ant-select-selection-placeholder {
    inset-inline-start: 12px;
  }
`;

const GroupBadge = ({ group }) => (
  <Tag
    color={GROUP_COLORS[group] || 'default'}
    icon={GROUP_ICONS[group]}
    style={{ marginRight: 8, fontSize: 10, textTransform: 'uppercase', lineHeight: '16px' }}
  >
    {GROUP_ICONS[group] ? ' ' : ''}{group}
  </Tag>
);

const MasterSearch = () => {
  const dispatch = useDispatch();
  const filtersdata = useSelector((state) => state.Filterdata.data);

  const persons = useSelector((state) => state.Persons.list);
  const companies = useSelector((state) => state.Companies.list);
  const categories = useSelector((state) => state.Categories.list);
  const projects = useSelector((state) => state.Projects.list);
  const tags = useSelector((state) => state.Tags.list);
  const accounts = useSelector((state) => state.Account.list);

  const personsById = useSelector((state) => state.Persons.byId);
  const companiesById = useSelector((state) => state.Companies.byId);
  const categoriesById = useSelector((state) => state.Categories.byId);
  const projectsById = useSelector((state) => state.Projects.byId);
  const tagsById = useSelector((state) => state.Tags.byId);
  const accountsById = useSelector((state) => state.Account.byId);

  const [searchText, setSearchText] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Registry: single source of truth describing every filter — how to build its
  // suggestions and how to apply/remove a selection.
  const registry = useMemo(() => {
    const entity = (key, group, list, byId) => ({
      key,
      group,
      kind: 'array',
      options: withNoValue(key, utils.processBackendOpts(list || [])),
      labelOf: (raw) => (utils.isNoValue(raw) ? NO_VALUE_LABEL : byId?.[raw]?.title ?? raw),
    });
    return [
      entity('persons', 'Oseba', persons, personsById),
      entity('companies', 'Podjetje', companies, companiesById),
      entity('projects', 'Projekt', projects, projectsById),
      entity('category', 'Kategorija', categories, categoriesById),
      { key: 'tags_filters', group: 'Oznaka', kind: 'array',
        options: withNoValue('tags_filters', utils.buildTagOptions(tags || [])),
        labelOf: (raw) => (utils.isNoValue(raw) ? NO_VALUE_LABEL : tagsById?.[raw]?.title ?? raw) },
      entity('account', 'Račun', accounts, accountsById),
      { key: 'type', group: 'Tip transakcije', kind: 'array',
        options: TYPE_OPTS.map((o) => ({ value: o.value, label: o.label })),
        labelOf: (raw) => TYPE_OPTS.find((o) => o.value === raw)?.label ?? raw },
      { key: 'is_paid', group: 'Plačano', kind: 'scalar',
        options: YESNO.map((o) => ({ value: o.value, label: o.title, title: o.title })),
        labelOf: (raw) => (isTrue(raw) ? 'Da' : 'Ne') },
      { key: 'is_issued', group: 'Izdano', kind: 'scalar',
        options: YESNO.map((o) => ({ value: o.value, label: o.title, title: o.title })),
        labelOf: (raw) => (isTrue(raw) ? 'Da' : 'Ne') },
    ];
  }, [persons, companies, categories, projects, tags, accounts,
      personsById, companiesById, categoriesById, projectsById, tagsById, accountsById]);

  const byKey = useMemo(() => Object.fromEntries(registry.map((r) => [r.key, r])), [registry]);

  // Current status pseudo-filter value derived from the two leaf booleans.
  const statusValue = isTrue(filtersdata.is_flagged)
    ? 'flagged'
    : isTrue(filtersdata.has_warning)
      ? 'warning'
      : null;

  // Build the Select's controlled value (array of tokens) + a label map, both
  // derived from Filterdata.data so the box always mirrors the live filter state.
  const { value, labelMap } = useMemo(() => {
    const tokens = [];
    const labels = {};
    const push = (key, raw, group, label) => {
      const t = makeToken(key, raw);
      tokens.push(t);
      labels[t] = `${group}: ${label}`;
    };

    registry.forEach((r) => {
      const cur = filtersdata[r.key];
      if (r.kind === 'array') {
        (Array.isArray(cur) ? cur : cur != null ? [cur] : []).forEach((raw) =>
          push(r.key, raw, r.group, r.labelOf(raw)));
      } else if (cur != null && cur !== '') {
        push(r.key, cur, r.group, r.labelOf(cur));
      }
    });

    // Status pseudo-filter chip.
    if (statusValue) {
      const s = STATUS_VALUES.find((x) => x.value === statusValue);
      push('status', statusValue, 'Stanje', s?.title ?? statusValue);
    }

    // Free-text search chip.
    if (filtersdata.search) {
      push('search', filtersdata.search, 'Iskanje', `"${filtersdata.search}"`);
    }

    return { value: tokens, labelMap: labels };
  }, [registry, filtersdata, statusValue]);

  // Build a single FLAT, diacritic-insensitive suggestion list (no group-header
  // rows). Best matches float to the top; each item keeps its own type badge.
  const options = useMemo(() => {
    const raw = searchText.trim();
    const q = normalize(raw);

    // Collect every candidate across all filters into one pool.
    const candidates = [];
    registry.forEach((r) => {
      (r.options || []).forEach((o) =>
        candidates.push({ key: r.key, group: r.group, rawValue: o.value, title: String(o.label), data: o }));
    });
    STATUS_VALUES.forEach((s) =>
      candidates.push({ key: 'status', group: 'Stanje', rawValue: s.value, title: s.title }));

    // Score against the option title AND the filter's group label, so typing a
    // filter name (e.g. "kategorija", "oznaka") surfaces all of its options.
    // Title: 0 = starts with query, 1 = a word starts with it, 2 = appears
    // anywhere. Group-name match scores 1 (so direct title hits still rank first).
    // Lowest (best) score wins; non-matches are dropped.
    const scored = [];
    candidates.forEach((c) => {
      if (!q) { scored.push({ ...c, score: 0 }); return; }
      const nt = normalize(c.title);
      let score = Infinity;
      if (nt.startsWith(q)) score = 0;
      else if (nt.split(/\s+/).some((w) => w.startsWith(q))) score = 1;
      else if (nt.includes(q)) score = 2;
      if (normalize(c.group).startsWith(q)) score = Math.min(score, 1);
      if (score === Infinity) return;
      scored.push({ ...c, score });
    });
    scored.sort((a, b) =>
      a.score - b.score ||
      a.group.localeCompare(b.group, 'sl') ||
      a.title.localeCompare(b.title, 'sl'));

    const result = [];
    // Top "free text" row, mirroring the reference UI.
    if (raw) {
      result.push({
        value: makeToken('search', raw),
        title: raw,
        label: <span><GroupBadge group="Iskanje" />Išči „{raw}“</span>,
      });
    }
    // Window the (possibly large) result set; grown on scroll for infinite paging.
    scored.slice(0, visibleCount).forEach((c) => {
      const icon = valueIconFor(c.key, c.rawValue, c.data);
      result.push({
        value: makeToken(c.key, c.rawValue),
        title: c.title,
        label: (
          <span>
            <GroupBadge group={c.group} />
            {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
            {c.title}
          </span>
        ),
      });
    });
    return { items: result, total: scored.length };
  }, [registry, searchText, visibleCount]);

  const handleSearch = (text) => {
    setSearchText(text);
    setVisibleCount(PAGE_SIZE); // restart paging whenever the query changes
  };

  // Shared clear: identical behaviour to the classic filters' clear button.
  const handleClear = () => {
    setSearchText('');
    setVisibleCount(PAGE_SIZE);
    dispatch(clearFilterData());
  };

  // Infinite scroll: grow the window as the user nears the bottom of the list.
  const handlePopupScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 80 && visibleCount < options.total) {
      setVisibleCount((c) => c + PAGE_SIZE);
    }
  };

  const applyArray = (key, raw, add) => {
    // Coerce to number when the option values are numeric (entity ids).
    const coerced = /^\d+$/.test(raw) ? Number(raw) : raw;
    const cur = Array.isArray(filtersdata[key]) ? filtersdata[key] : [];
    const next = add
      ? [...cur, coerced]
      : cur.filter((x) => String(x) !== String(raw));
    dispatch(updateFilterData({ [key]: next }));
  };

  const handleSelect = (token) => {
    setSearchText('');
    setVisibleCount(PAGE_SIZE);
    const { key, value: raw } = parseToken(token);
    if (key === 'search') {
      dispatch(updateFilterData({ search: raw }));
      return;
    }
    if (key === 'status') {
      const s = STATUS_VALUES.find((x) => x.value === raw);
      dispatch(updateFilterData(s ? s.filter : { is_flagged: null, has_warning: null }));
      return;
    }
    const r = byKey[key];
    if (!r) return;
    if (r.kind === 'array') {
      applyArray(key, raw, true);
    } else {
      const coerced = /^-?\d+$/.test(raw) ? Number(raw) : raw;
      dispatch(updateFilterData({ [key]: coerced }));
    }
  };

  const handleDeselect = (token) => {
    const { key, value: raw } = parseToken(token);
    if (key === 'search') {
      dispatch(updateFilterData({ search: null }));
      return;
    }
    if (key === 'status') {
      dispatch(updateFilterData({ is_flagged: null, has_warning: null }));
      return;
    }
    const r = byKey[key];
    if (!r) return;
    if (r.kind === 'array') {
      applyArray(key, raw, false);
    } else {
      dispatch(updateFilterData({ [key]: null }));
    }
  };

  return (
    <SearchWrapper>
      <Button
        style={{ height: 36, width: 36, borderRadius: 8 }}
        onClick={handleClear}
        title="Počisti filtre"
      >
        <StopOutlined />
      </Button>
      <Select
        size="middle"
        mode="multiple"
        value={value}
        options={options.items}
        onSelect={handleSelect}
        onDeselect={handleDeselect}
        onSearch={handleSearch}
        onPopupScroll={handlePopupScroll}
        searchValue={searchText}
        filterOption={false}
        suffixIcon={<SearchOutlined />}
        placeholder="Išči po osebah, podjetjih, projektih, oznakah …"
        notFoundContent={searchText ? 'Ni zadetkov' : null}
        tagRender={({ value: token, onClose }) => (
          <Tag closable onClose={onClose} style={{ marginInlineEnd: 4 }}>
            {labelMap[token] ?? token}
          </Tag>
        )}
      />
    </SearchWrapper>
  );
};

export default MasterSearch;
